import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { useWorkspacesStore } from '@common/modules/workspaces'

export type VisionMode = 'local' | 'hosted'

const DEFAULT_LOADER_MESSAGE = 'Loading ...'
const visionMode = (process.env.NEXT_PUBLIC_VISION_MODE as VisionMode) || 'local'

type LayoutState = {
  mobileMenuOpen: boolean
  createProjectDialogOpen: boolean
  inviteProjectOpen: boolean
  feedbackPopoverOpen: boolean
  inviteWorkspaceOpen: boolean
  sidebarCollapsed: boolean
  isLoading: boolean
  loaderMessage: string
  loaderCount: number
  visionMode: VisionMode
  isVisionLocal: boolean
}

type LayoutStore = LayoutState & {
  setState: (patch: Partial<LayoutState>) => void
  toggleSidebar: () => void
  startLoader: (message?: string) => void
  stopLoader: () => void
  clearFullScreenLoader: () => void
}

const initialState: LayoutState = {
  mobileMenuOpen: false,
  createProjectDialogOpen: false,
  inviteProjectOpen: false,
  feedbackPopoverOpen: false,
  inviteWorkspaceOpen: false,
  sidebarCollapsed: false,
  isLoading: true,
  loaderMessage: DEFAULT_LOADER_MESSAGE,
  loaderCount: 1,
  visionMode,
  isVisionLocal: visionMode === 'local',
}

export const useLayoutStore = create<LayoutStore>()(
  devtools(
    immer((set) => ({
      ...initialState,
      setState: (patch) =>
        set((state) => {
          Object.assign(state, patch)
        }),
      toggleSidebar: () =>
        set((state) => {
          state.sidebarCollapsed = !state.sidebarCollapsed
        }),
      startLoader: (message?: string) =>
        set((state) => {
          state.loaderCount += 1
          state.loaderMessage = message ?? DEFAULT_LOADER_MESSAGE
          state.isLoading = true
        }),
      stopLoader: () =>
        set((state) => {
          state.loaderCount = Math.max(0, state.loaderCount - 1)
          if (state.loaderCount === 0) {
            state.isLoading = false
            state.loaderMessage = DEFAULT_LOADER_MESSAGE
          }
        }),
      clearFullScreenLoader: () =>
        set((state) => {
          state.loaderCount = 0
          state.isLoading = false
          state.loaderMessage = DEFAULT_LOADER_MESSAGE
        }),
    })),
    { name: 'LayoutStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

useWorkspacesStore.subscribe(
  (state) => state.createProjectForWorkspaceId,
  (createProjectForWorkspaceId) => {
    if (createProjectForWorkspaceId) {
      useLayoutStore.getState().setState({ createProjectDialogOpen: true })
    }
  }
)
